import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment/moment";
import { useFormikContext } from "formik";
import { IconChevronDown, IconReceipt } from "@tabler/icons-react";
import Tooltip from "../../UI/Tooltip";
// @ts-expect-error pure js
import { Interrogation } from "../../UI/Icons.jsx";
// @ts-expect-error pure js
import TextInput from "../../UI/TextInput";
import Card from "../../UI/Card";
import Callout from "../../UI/Callout";
import Button from "../../UI/Button";
import Text, { type Color } from "../../UI/Text";
import Title from "../../UI/Title";
import Status from "../../UI/Status";
import FeaturedIcon from "../../UI/FeaturedIcon";
// @ts-expect-error pure js
import IsLoading from "../../UI/IsLoading";
// @ts-expect-error pure js
import { currencyFormatter } from "../../../api/Functions";
// @ts-expect-error pure js
import { useBalanceChargesRecuperables } from "../../../hooks/use-balances";
import { Selectable } from "../../UI/Selectable";
import BalanceRecuperableCard, {
  type BalanceRecuperable,
} from "./BalanceRecuperableCard";
import { calculateProrata } from "../../../utils/balances";
// @ts-expect-error pure js
import { classnames } from "../../../utils";
import type { Balance } from "../../../models/balance";
import type { Tenant } from "../../../models/tenant";
import type { Property } from "../../../models/property";
import styles from "./RegulForm.module.css";

interface RegulFormValues {
  debut_periode?: string;
  fin_periode?: string;
  charges_percues?: number;
  charges_payees?: number;
  charges_complementaires?: number | "";
  balances?: (number | string)[];
}

interface RegulFormProps {
  balances: Balance[];
  property: Property;
  tenant: Tenant;
}

type SoldeSens = "remboursement" | "complement" | "equilibre";

const SOLDE_SENS: Record<SoldeSens, { color: Color; badge: string }> = {
  remboursement: { color: "info", badge: "Remboursement au locataire" },
  complement: { color: "warning", badge: "Complément dû par le locataire" },
  equilibre: { color: "neutral", badge: "Aucune régularisation" },
};

function roundCents(value: number) {
  return Math.round(value * 100) / 100;
}

function isInPeriode(
  balance: Pick<Balance, "date_transac">,
  debut?: string,
  fin?: string,
) {
  return moment(balance.date_transac).isBetween(debut, fin, "day", "[]");
}

export default function RegulForm({
  balances,
  property,
  tenant,
}: RegulFormProps) {
  const { values, setFieldValue } = useFormikContext<RegulFormValues>();
  const finPeriode = values.fin_periode;
  const debutPeriode = values.debut_periode;
  const [showDetailPayees, setShowDetailPayees] = useState(false);
  const [showDetailPercues, setShowDetailPercues] = useState(false);

  const { balanceRecuperables, isLoading } = useBalanceChargesRecuperables(
    property.id,
    tenant.id,
  ) as { balanceRecuperables: Balance[]; isLoading: boolean };
  const [selectedBalances, setSelectedBalances] = useState<
    Set<number | string>
  >(new Set());

  const balancePeriode = balances.filter((balance) =>
    isInPeriode(balance, debutPeriode, finPeriode),
  );
  // Seule la part récupérable d'une charge se refacture (la TEOM d'une taxe foncière, la
  // part locataire d'un appel de fonds) : c'est elle que porte `montant` ici.
  const balancesWithProrata = useMemo<BalanceRecuperable[]>(
    () =>
      balanceRecuperables
        .map((balance) => {
          const recuperable = {
            ...balance,
            montant: balance.montant_recuperable ?? balance.montant,
          };
          return {
            ...recuperable,
            montantProratise: calculateProrata(recuperable, tenant),
          };
        })
        .sort((a, b) => moment(a.date_transac).diff(b.date_transac)),
    [balanceRecuperables, tenant],
  );

  const balanceRecuperablesPeriode = balancesWithProrata.filter((balance) =>
    isInPeriode(balance, debutPeriode, finPeriode),
  );
  const balanceRecuperablesAvantPeriode = balancesWithProrata.filter(
    (balance) =>
      debutPeriode &&
      moment(balance.date_transac).isBefore(debutPeriode, "day"),
  );
  const balanceRecuperablesApresPeriode = balancesWithProrata.filter(
    (balance) =>
      finPeriode && moment(balance.date_transac).isAfter(finPeriode, "day"),
  );
  const balanceRecuperablesPeriodeIds = balanceRecuperablesPeriode.map(
    (balance) => balance.id,
  );
  const chargesPercues = Math.abs(
    balancePeriode.reduce((sum, balance) => sum + balance.montant, 0),
  );

  const totalChargesPayees = Math.abs(
    balancesWithProrata
      .filter((balance) => selectedBalances.has(balance.id))
      .reduce((sum, balance) => sum + balance.montantProratise, 0),
  );
  const chargesComplementaires = Number(values.charges_complementaires) || 0;
  const solde = roundCents(
    chargesPercues - (totalChargesPayees + chargesComplementaires),
  );
  const sens: SoldeSens =
    solde > 0 ? "remboursement" : solde < 0 ? "complement" : "equilibre";

  useEffect(() => {
    setFieldValue("charges_percues", chargesPercues);
  }, [chargesPercues, setFieldValue]);
  useEffect(() => {
    setFieldValue("charges_payees", totalChargesPayees);
  }, [totalChargesPayees, setFieldValue]);
  useEffect(() => {
    setFieldValue("balances", [...selectedBalances]);
  }, [selectedBalances, setFieldValue]);

  if (isLoading) {
    return <IsLoading />;
  }

  return (
    <div className={styles.root}>
      <div>
        <Title variant="hecto" weight="bold">
          Régularisation de charges
        </Title>
        <Text>
          Comparez les provisions appelées aux charges réelles sur la période
          pour calculer le solde à régulariser.
        </Text>
      </div>

      {tenant.type_charge === "forfait" && (
        <Callout variant="warning">
          La régularisation ne concerne que les charges locatives qui font
          l’objet d’une provision sur charges. Il n’est pas possible de faire
          une régularisation sur des charges locatives soumises à un forfait de
          charge.
        </Callout>
      )}

      <section className={styles.section}>
        <Text as="div" weight="bold" className={styles.sectionTitle}>
          Période de régularisation
          <Tooltip
            content={
              <>
                Une régularisation de charge ne peut être effectuée que sur{" "}
                <strong>les 3 dernières années maximum</strong>. Vous ne pouvez
                pas réclamer des charges récupérables plus anciennes.
              </>
            }
          >
            <Interrogation />
          </Tooltip>
        </Text>
        <div className={styles.dateGrid}>
          <TextInput
            name="debut_periode"
            type="date"
            label="Début de la période"
            marginForm=""
            mandatory
          />
          <TextInput
            name="fin_periode"
            type="date"
            label="Fin de la période"
            marginForm=""
            mandatory
          />
        </div>
      </section>

      <Card padding="md" className={styles.amountCard}>
        <FeaturedIcon icon={IconReceipt} shape="square" color="primary" />
        <div className={styles.amountMain}>
          <Text variant="kilo" weight="bold">
            {currencyFormatter(chargesPercues)}
          </Text>
          <Text color="muted" className={styles.amountLabel}>
            Charges appelées en provision sur la période
            <Tooltip content="Prend en compte tous les éléments de l'historique des transactions dont le type est « Appel de charge ».">
              <Interrogation />
            </Tooltip>
          </Text>
          <Button
            variant="tertiary"
            type="button"
            icon={IconChevronDown}
            className={classnames(
              styles.toggle,
              showDetailPercues && styles.toggleOpen,
            )}
            onClick={() => setShowDetailPercues((prev) => !prev)}
          >
            {showDetailPercues ? "Masquer le détail" : "Voir le détail"}
          </Button>
          {showDetailPercues && (
            <div className={styles.detail}>
              {balancePeriode.length === 0 ? (
                <Text color="muted">
                  Aucun appel de charge sur la période sélectionnée.
                </Text>
              ) : (
                <ul className={styles.detailList}>
                  {balancePeriode.map((balance) => (
                    <li key={balance.id} className={styles.detailRow}>
                      <Text as="span" color="muted">
                        {moment(balance.date_transac).format("DD/MM/YYYY")}
                        {balance.comment && (
                          <Tooltip content={balance.comment}>
                            <Interrogation />
                          </Tooltip>
                        )}
                      </Text>
                      <Text as="span">
                        {currencyFormatter(Math.abs(balance.montant))}
                      </Text>
                    </li>
                  ))}
                  <li
                    className={classnames(styles.detailRow, styles.detailTotal)}
                  >
                    <Text as="span" weight="bold">
                      Total provisions
                    </Text>
                    <Text as="span" weight="bold">
                      {currencyFormatter(chargesPercues)}
                    </Text>
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card padding="md" className={styles.amountCard}>
        <FeaturedIcon icon={IconReceipt} shape="square" color="violet" />
        <div className={styles.amountMain}>
          <Text variant="kilo" weight="bold">
            {currencyFormatter(totalChargesPayees)}
          </Text>
          <Text color="muted" className={styles.amountLabel}>
            Charges comptabilisées et payées par le bailleur
            <Tooltip content="Prend en compte tous les éléments de la comptabilité du bien dont la catégorie est une charge récupérable n'ayant pas encore été récupérée.">
              <Interrogation />
            </Tooltip>
          </Text>
          <Button
            variant="tertiary"
            type="button"
            icon={IconChevronDown}
            className={classnames(
              styles.toggle,
              showDetailPayees && styles.toggleOpen,
            )}
            onClick={() => setShowDetailPayees((prev) => !prev)}
          >
            {showDetailPayees ? "Masquer le détail" : "Voir le détail"}
          </Button>
          <Selectable
            items={balancesWithProrata}
            defaultSelectedIds={balanceRecuperablesPeriodeIds}
            onChange={setSelectedBalances}
          >
            {showDetailPayees && (
              <div className={styles.detail}>
                {balancesWithProrata.length === 0 ? (
                  <Text color="muted">
                    Aucune charge récupérable en attente de refacturation pour
                    ce bien.
                  </Text>
                ) : (
                  <>
                    <label className={styles.selectAll}>
                      <Selectable.Checkbox selectAll />
                      <Text as="span" weight="medium">
                        Tout sélectionner
                      </Text>
                    </label>
                    {balanceRecuperablesAvantPeriode.length > 0 && (
                      <div className={styles.group}>
                        <Text color="muted">Avant la période sélectionnée</Text>
                        {balanceRecuperablesAvantPeriode.map((balance, i) => (
                          <BalanceRecuperableCard
                            key={balance.id}
                            balance={balance}
                            index={i}
                          />
                        ))}
                      </div>
                    )}
                    {balanceRecuperablesPeriode.length > 0 && (
                      <div className={styles.group}>
                        <Text color="muted">Sur la période sélectionnée</Text>
                        {balanceRecuperablesPeriode.map((balance, i) => (
                          <BalanceRecuperableCard
                            key={balance.id}
                            balance={balance}
                            index={i + balanceRecuperablesAvantPeriode.length}
                          />
                        ))}
                      </div>
                    )}
                    {balanceRecuperablesApresPeriode.length > 0 && (
                      <div className={styles.group}>
                        <Text color="muted">Après la période sélectionnée</Text>
                        {balanceRecuperablesApresPeriode.map((balance, i) => (
                          <BalanceRecuperableCard
                            key={balance.id}
                            balance={balance}
                            index={
                              i +
                              balanceRecuperablesAvantPeriode.length +
                              balanceRecuperablesPeriode.length
                            }
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </Selectable>
        </div>
      </Card>

      <TextInput
        type="number"
        name="charges_complementaires"
        placeholder="0"
        step="0.01"
        unit="€"
        marginForm=""
        label={
          <>
            Des charges n'apparaissent pas ? Ajoutez les montants manquants{" "}
            <Tooltip
              content={
                <>
                  Attention, les charges récupérables auprès du locataire sont
                  strictement limitées par la loi.
                  <br />
                  Le montant des charges récupérables figure généralement sur le
                  décompte de charges trimestrielles adressé par le syndic où il
                  est distingué "part propriétaire" et "part locataire".
                  <br />
                  Vous pouvez également retrouver la liste détaillée{" "}
                  <Link
                    to="https://www.legifrance.gouv.fr/loda/id/LEGITEXT000006066149/"
                    target="_blank"
                  >
                    ici
                  </Link>
                  .<br />
                  Si vous n'avez pas entré toutes les charges dans Qalimo, vous
                  pouvez ajouter ici la somme des montants manquants.
                </>
              }
            >
              <Interrogation />
            </Tooltip>
          </>
        }
      />

      {debutPeriode && finPeriode && (
        <Card padding="md" className={styles.recap}>
          <dl className={styles.recapRows}>
            <div className={styles.recapRow}>
              <Text as="dt" color="muted">
                Provisions appelées sur la période
              </Text>
              <Text as="dd" weight="medium">
                {currencyFormatter(chargesPercues)}
              </Text>
            </div>
            <div className={styles.recapRow}>
              <Text as="dt" color="muted">
                Charges comptabilisées et payées
              </Text>
              <Text as="dd" weight="medium">
                − {currencyFormatter(totalChargesPayees)}
              </Text>
            </div>
            {chargesComplementaires > 0 && (
              <div className={styles.recapRow}>
                <Text as="dt" color="muted">
                  Montants manquants ajoutés
                </Text>
                <Text as="dd" weight="medium">
                  − {currencyFormatter(chargesComplementaires)}
                </Text>
              </div>
            )}
          </dl>
          <div className={classnames(styles.solde, styles[`solde-${sens}`])}>
            <div className={styles.soldeLabel}>
              <Text weight="bold">Solde à régulariser</Text>
              <Status
                color={SOLDE_SENS[sens].color}
                weight="medium"
                text={SOLDE_SENS[sens].badge}
              />
            </div>
            <Text
              as="span"
              variant="kilo"
              weight="bold"
              color={sens === "equilibre" ? "muted" : SOLDE_SENS[sens].color}
            >
              {currencyFormatter(Math.abs(solde))}
            </Text>
          </div>
        </Card>
      )}
    </div>
  );
}
